import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Menu, X, Phone, ShoppingBag, LogIn, User, LogOut, LayoutDashboard, FileText } from "lucide-react";
import { BRAND, NAV_LINKS } from "../data/content";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";
import { HolidayBanner } from "./holiday/HolidayBanner";

const renderLink = (l) => {
  // External anchor inside homepage starts with "/#"
  const isHomeAnchor = l.href.startsWith("/#");
  if (isHomeAnchor) {
    return l; // use <a>
  }
  return { ...l, isRoute: true };
};

export const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [userOpen, setUserOpen] = useState(false);
  const { itemCount, openDrawer } = useCart();
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const closeMenu = () => setOpen(false);
  const isLoggedIn = user && typeof user === "object";

  const renderNavLink = (l, mobile = false) => {
    const link = renderLink(l);
    const className = mobile
      ? "py-3 px-3 rounded-xl text-[15px] font-semibold text-brand-charcoal hover:bg-brand-cream transition-colors"
      : "text-[12px] xl:text-[13px] 2xl:text-[13.5px] font-semibold text-brand-charcoal/80 hover:text-brand-charcoal transition-colors whitespace-nowrap relative after:content-[''] after:absolute after:-bottom-1.5 after:left-0 after:h-[2px] after:w-0 after:bg-brand-teal after:transition-all hover:after:w-full";
    const testid = `${mobile ? "mobile-" : ""}nav-link-${l.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`;
    if (link.isRoute) {
      return (
        <Link
          key={l.href}
          to={l.href}
          data-testid={testid}
          className={className}
          onClick={mobile ? closeMenu : undefined}
        >
          {l.label}
        </Link>
      );
    }
    return (
      <a
        key={l.href}
        href={l.href}
        data-testid={testid}
        className={className}
        onClick={mobile ? closeMenu : undefined}
      >
        {l.label}
      </a>
    );
  };

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-white/85 backdrop-blur-xl border-b border-black/5 shadow-[0_4px_24px_-12px_rgba(0,0,0,0.12)]"
          : "bg-white/70 backdrop-blur-md border-b border-transparent"
      }`}
    >
      <HolidayBanner hidden={scrolled} />
      <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-10">
        <div className="h-[64px] sm:h-[72px] flex items-center justify-between gap-3 sm:gap-6">
          <Link
            to="/"
            data-testid="header-logo-link"
            className="flex items-center gap-2.5 sm:gap-3 shrink-0 min-w-0"
            aria-label="Fresh Fast Foods LLC home"
          >
            <img
              src={BRAND.logo}
              alt="Fresh Fast Foods LLC logo"
              className="h-10 w-10 sm:h-11 sm:w-11 object-contain shrink-0"
            />
            <div className="flex flex-col leading-tight min-w-0">
              <span className="font-display font-extrabold text-[13.5px] sm:text-[15px] text-brand-charcoal tracking-tight truncate">
                Fresh Fast Foods
              </span>
              <span className="text-[9.5px] sm:text-[10px] font-semibold uppercase tracking-[0.16em] sm:tracking-[0.18em] text-brand-teal truncate">
                Catering · NYC
              </span>
            </div>
          </Link>

          <nav className="hidden lg:flex items-center gap-3 xl:gap-5 2xl:gap-6" aria-label="Main">
            {NAV_LINKS.map((l) => {
              // Hide secondary anchor links between lg–xl so the bar fits without wrapping.
              const hideOnLg = l.href === "/#community" || l.href === "/who-we-serve" || l.href === "/partner";
              const wrapper = hideOnLg ? "hidden xl:inline-flex" : "inline-flex";
              return (
                <span key={l.href} className={wrapper}>
                  {renderNavLink(l)}
                </span>
              );
            })}
          </nav>

          <div className="hidden lg:flex items-center gap-1.5 xl:gap-2.5">
            <a
              href={`tel:${BRAND.phoneTel}`}
              data-testid="header-call-now-btn"
              className="inline-flex items-center gap-1.5 text-[12px] xl:text-[13px] font-bold uppercase tracking-wider text-brand-charcoal hover:text-brand-teal transition-colors whitespace-nowrap"
              aria-label="Call Fresh Fast Foods"
            >
              <Phone className="h-4 w-4" />
              <span className="hidden xl:inline">Call</span>
            </a>

            <CartButton itemCount={itemCount} onOpen={openDrawer} />

            {isLoggedIn ? (
              <div className="relative">
                <button
                  data-testid="header-user-btn"
                  onClick={() => setUserOpen((v) => !v)}
                  className="inline-flex items-center gap-1.5 rounded-full bg-brand-cream border border-black/5 px-2 xl:px-2.5 py-1.5 text-[11px] xl:text-[11.5px] font-bold text-brand-charcoal hover:bg-white transition-colors"
                >
                  <User className="h-3 w-3" />
                  <span className="hidden xl:inline max-w-[80px] truncate">{user.name?.split(" ")[0] || user.email}</span>
                  {user.role === "agency" && (
                    <span className="rounded-full bg-brand-teal text-white px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider">Agency</span>
                  )}
                  {user.role === "admin" && (
                    <span className="rounded-full bg-brand-gold text-brand-charcoal px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider">Admin</span>
                  )}
                </button>
                {userOpen && (
                  <div
                    data-testid="header-user-menu"
                    onMouseLeave={() => setUserOpen(false)}
                    className="absolute right-0 mt-2 w-48 rounded-2xl bg-white border border-black/5 shadow-[0_18px_40px_-18px_rgba(0,0,0,0.25)] p-1.5 z-50"
                  >
                    {user.role === "admin" && (
                      <Link
                        to="/admin/orders"
                        data-testid="header-admin-link"
                        onClick={() => setUserOpen(false)}
                        className="w-full text-left flex items-center gap-2 rounded-xl px-3 py-2.5 text-[12.5px] font-bold text-brand-charcoal hover:bg-brand-cream"
                      >
                        <LayoutDashboard className="h-3.5 w-3.5" />
                        Admin
                      </Link>
                    )}
                    {(user.role === "agency" || user.role === "admin") && (
                      <Link
                        to="/account/invoices"
                        data-testid="header-invoices-link"
                        onClick={() => setUserOpen(false)}
                        className="w-full text-left flex items-center gap-2 rounded-xl px-3 py-2.5 text-[12.5px] font-bold text-brand-charcoal hover:bg-brand-cream"
                      >
                        <FileText className="h-3.5 w-3.5" />
                        My Invoices
                      </Link>
                    )}
                    <button
                      type="button"
                      data-testid="header-logout-btn"
                      onClick={async () => { setUserOpen(false); await logout(); navigate("/"); }}
                      className="w-full text-left flex items-center gap-2 rounded-xl px-3 py-2.5 text-[12.5px] font-bold text-brand-charcoal hover:bg-brand-cream"
                    >
                      <LogOut className="h-3.5 w-3.5" />
                      Sign out
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <Link
                to="/login"
                data-testid="header-login-btn"
                className="inline-flex items-center gap-1.5 rounded-full border border-brand-charcoal/20 px-3 xl:px-3.5 py-2 text-[11px] xl:text-[12px] font-bold uppercase tracking-[0.08em] xl:tracking-[0.1em] text-brand-charcoal hover:bg-brand-charcoal hover:text-white transition-colors whitespace-nowrap"
              >
                <LogIn className="h-3.5 w-3.5" />
                Sign in
              </Link>
            )}

            <Link
              to="/menu"
              data-testid="header-request-quote-btn"
              className="inline-flex items-center justify-center rounded-full bg-brand-teal text-white px-3.5 xl:px-4 py-2.5 text-[11px] xl:text-[12px] font-bold uppercase tracking-[0.06em] xl:tracking-[0.08em] hover:bg-brand-teal-dark transition-all hover:scale-[1.03] shadow-[0_8px_24px_-10px_rgba(0,166,178,0.7)] whitespace-nowrap"
            >
              Order Now
            </Link>
          </div>

          {/* Mobile right side: cart + toggle */}
          <div className="lg:hidden flex items-center gap-2">
            <CartButton itemCount={itemCount} onOpen={openDrawer} compact />
            <button
              data-testid="header-mobile-toggle"
              aria-label={open ? "Close menu" : "Open menu"}
              onClick={() => setOpen((v) => !v)}
              className="inline-flex items-center justify-center h-10 w-10 sm:h-11 sm:w-11 rounded-full border border-black/10 bg-white text-brand-charcoal hover:bg-brand-cream transition-colors shrink-0"
            >
              {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile sheet */}
      <div
        className={`lg:hidden overflow-hidden transition-[max-height,opacity] duration-300 ${
          open ? "max-h-[720px] opacity-100" : "max-h-0 opacity-0"
        } bg-white border-t border-black/5`}
        data-testid="header-mobile-menu"
      >
        <nav className="px-5 py-5 flex flex-col gap-1" aria-label="Mobile">
          {NAV_LINKS.map((l) => renderNavLink(l, true))}

          <div className="h-px bg-black/5 my-2" />

          {isLoggedIn ? (
            <>
              {user.role === "admin" && (
                <Link
                  to="/admin/orders"
                  onClick={closeMenu}
                  data-testid="mobile-admin-link"
                  className="py-3 px-3 rounded-xl text-[15px] font-semibold text-brand-charcoal hover:bg-brand-cream flex items-center gap-2"
                >
                  <LayoutDashboard className="h-4 w-4" /> Admin Dashboard
                </Link>
              )}
              {(user.role === "agency" || user.role === "admin") && (
                <Link
                  to="/account/invoices"
                  onClick={closeMenu}
                  data-testid="mobile-invoices-link"
                  className="py-3 px-3 rounded-xl text-[15px] font-semibold text-brand-charcoal hover:bg-brand-cream flex items-center gap-2"
                >
                  <FileText className="h-4 w-4" /> My Invoices
                </Link>
              )}
              <button
                type="button"
                data-testid="mobile-logout-btn"
                onClick={async () => { closeMenu(); await logout(); navigate("/"); }}
                className="py-3 px-3 rounded-xl text-[15px] font-semibold text-brand-charcoal hover:bg-brand-cream text-left flex items-center gap-2"
              >
                <LogOut className="h-4 w-4" /> Sign out ({user.name?.split(" ")[0]})
              </button>
            </>
          ) : (
            <Link
              to="/login"
              onClick={closeMenu}
              data-testid="mobile-login-btn"
              className="py-3 px-3 rounded-xl text-[15px] font-semibold text-brand-charcoal hover:bg-brand-cream flex items-center gap-2"
            >
              <LogIn className="h-4 w-4" /> Sign in
            </Link>
          )}

          <div className="grid grid-cols-2 gap-3 pt-3">
            <a
              href={`tel:${BRAND.phoneTel}`}
              onClick={closeMenu}
              data-testid="mobile-call-now-btn"
              className="inline-flex items-center justify-center gap-2 rounded-full border-2 border-brand-charcoal px-4 py-3 text-[12px] font-bold uppercase tracking-wider text-brand-charcoal hover:bg-brand-charcoal hover:text-white transition-colors"
            >
              <Phone className="h-4 w-4" /> Call
            </a>
            <Link
              to="/menu"
              onClick={closeMenu}
              data-testid="mobile-order-now-btn"
              className="inline-flex items-center justify-center rounded-full bg-brand-teal text-white px-4 py-3 text-[12px] font-bold uppercase tracking-wider hover:bg-brand-teal-dark transition-colors"
            >
              Order Now
            </Link>
          </div>
        </nav>
      </div>
    </header>
  );
};

const CartButton = ({ itemCount, onOpen, compact = false }) => (
  <button
    type="button"
    data-testid="header-cart-btn"
    onClick={onOpen}
    aria-label={`Open cart (${itemCount} items)`}
    className={`relative inline-flex items-center justify-center rounded-full border border-black/10 bg-white text-brand-charcoal hover:bg-brand-cream transition-colors ${
      compact ? "h-10 w-10" : "h-10 w-10"
    }`}
  >
    <ShoppingBag className="h-4 w-4" />
    {itemCount > 0 && (
      <span
        data-testid="header-cart-count"
        className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] inline-flex items-center justify-center rounded-full bg-brand-teal text-white text-[10px] font-bold px-1"
      >
        {itemCount}
      </span>
    )}
  </button>
);

export default Header;
